import React, { useCallback, useEffect, useState } from 'react';
import './App.css';
import { Gruppe, Session } from './types';
import { ApiService } from './api';
import GruppenListe from './components/GruppenListe';
import GruppeDetails from './components/GruppeDetails';
import SessionSpiel from './components/SessionSpiel';
import Leaderboard from './components/Leaderboard';
import BackupManager from './components/BackupManager';
import SettingsView from './components/SettingsView';
import TabBar, { TabItem } from './components/TabBar';
import NavBar from './components/NavBar';
import { ToastProvider, useToast } from './components/Toast';

type Tab = 'gruppen' | 'backup' | 'settings';

type GruppenStack =
  | { kind: 'list' }
  | { kind: 'detail'; gruppe: Gruppe }
  | { kind: 'session'; gruppe: Gruppe; session: Session }
  | { kind: 'leaderboard'; gruppe: Gruppe };

const tabs: TabItem<Tab>[] = [
  { id: 'gruppen', label: 'Gruppen', icon: 'home' },
  { id: 'backup', label: 'Backups', icon: 'archive' },
  { id: 'settings', label: 'Einstellungen', icon: 'gear' },
];

const Shell: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('gruppen');
  const [stack, setStack] = useState<GruppenStack>({ kind: 'list' });
  const [gruppen, setGruppen] = useState<Gruppe[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const loadGruppen = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ApiService.getGruppen();
      setGruppen(data);
      setStack((s) => {
        if (s.kind === 'detail') {
          const g = data.find((x) => x.id === s.gruppe.id);
          return g ? { kind: 'detail', gruppe: g } : { kind: 'list' };
        }
        return s;
      });
    } catch (err) {
      setError('Fehler beim Laden der Gruppen');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGruppen();
  }, [loadGruppen]);

  const refreshGruppe = useCallback(
    async (id: number) => {
      try {
        const g = await ApiService.getGruppe(id);
        setGruppen((prev) => prev.map((x) => (x.id === id ? g : x)));
        setStack((s) => {
          if (s.kind === 'detail' && s.gruppe.id === id) return { kind: 'detail', gruppe: g };
          if (s.kind === 'leaderboard' && s.gruppe.id === id) return { kind: 'leaderboard', gruppe: g };
          if (s.kind === 'session' && s.gruppe.id === id) return { ...s, gruppe: g };
          return s;
        });
        return g;
      } catch (e) {
        console.error(e);
        return null;
      }
    },
    []
  );

  const handleGruppeOpen = (g: Gruppe) => setStack({ kind: 'detail', gruppe: g });
  const handleBackToList = () => setStack({ kind: 'list' });
  const handleOpenSession = (session: Session) => {
    if (stack.kind === 'detail' || stack.kind === 'leaderboard' || stack.kind === 'session') {
      setStack({ kind: 'session', gruppe: stack.gruppe, session });
    }
  };
  const handleOpenLeaderboard = () => {
    if (stack.kind === 'detail') setStack({ kind: 'leaderboard', gruppe: stack.gruppe });
  };
  const handleBackToDetail = () => {
    if (stack.kind === 'session' || stack.kind === 'leaderboard') {
      setStack({ kind: 'detail', gruppe: stack.gruppe });
    }
  };
  const handleNewSession = async () => {
    if (stack.kind !== 'detail') return;
    try {
      const session = await ApiService.createSession(stack.gruppe.id);
      const g = await refreshGruppe(stack.gruppe.id);
      setStack({ kind: 'session', gruppe: g ?? stack.gruppe, session });
    } catch (e) {
      console.error(e);
      toast.error('Neue Session konnte nicht gestartet werden');
    }
  };

  const handleTabChange = (tab: Tab) => {
    if (tab === activeTab && tab === 'gruppen') {
      setStack({ kind: 'list' });
    } else {
      setActiveTab(tab);
    }
  };

  const renderGruppenTab = () => {
    if (loading) {
      return (
        <div className="loading-ios">
          <div className="spinner" />
          <div className="footnote">Lade…</div>
        </div>
      );
    }
    if (error) {
      return (
        <div className="empty-ios view-enter">
          <div className="empty-emoji">⚠️</div>
          <div className="empty-title">Das ist schiefgegangen</div>
          <p className="footnote" style={{ marginBottom: 14 }}>{error}</p>
          <button className="btn btn-primary" onClick={loadGruppen}>Erneut versuchen</button>
        </div>
      );
    }

    if (stack.kind === 'list') {
      return (
        <>
          <NavBar title="" />
          <div className="page">
            <div className="large-title-row">
              <h1 className="large-title">Gruppen</h1>
            </div>
            <GruppenListe
              gruppen={gruppen}
              onGruppeSelect={handleGruppeOpen}
              onRefresh={loadGruppen}
            />
          </div>
        </>
      );
    }

    if (stack.kind === 'detail') {
      return (
        <>
          <NavBar title={stack.gruppe.name} onBack={handleBackToList} backLabel="Gruppen" />
          <div className="page">
            <GruppeDetails
              gruppe={stack.gruppe}
              onSessionSelect={handleOpenSession}
              onNewSession={handleNewSession}
              onShowLeaderboard={handleOpenLeaderboard}
              onRefresh={(g) => {
                if (g) refreshGruppe(g.id);
                else loadGruppen();
              }}
            />
          </div>
        </>
      );
    }

    if (stack.kind === 'session') {
      return (
        <>
          <NavBar
            title={`Runde · ${stack.gruppe.name}`}
            onBack={handleBackToDetail}
            backLabel={stack.gruppe.name}
          />
          <div className="page page--session">
            <SessionSpiel
              session={stack.session}
              gruppe={stack.gruppe}
              onBack={handleBackToDetail}
              onRefresh={() => refreshGruppe(stack.gruppe.id)}
            />
          </div>
        </>
      );
    }

    if (stack.kind === 'leaderboard') {
      return (
        <>
          <NavBar title="Leaderboard" onBack={handleBackToDetail} backLabel={stack.gruppe.name} />
          <div className="page">
            <Leaderboard gruppe={stack.gruppe} onBack={handleBackToDetail} />
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <div className="App">
      <div className="ios-ambient" aria-hidden="true" />

      {activeTab === 'gruppen' && renderGruppenTab()}

      {activeTab === 'backup' && (
        <>
          <NavBar title="" />
          <div className="page">
            <div className="large-title-row">
              <h1 className="large-title">Backups</h1>
            </div>
            <BackupManager />
          </div>
        </>
      )}

      {activeTab === 'settings' && (
        <>
          <NavBar title="" />
          <div className="page">
            <div className="large-title-row">
              <h1 className="large-title">Einstellungen</h1>
            </div>
            <SettingsView />
          </div>
        </>
      )}

      <TabBar<Tab> items={tabs} active={activeTab} onChange={handleTabChange} />
    </div>
  );
};

function App() {
  return (
    <ToastProvider>
      <Shell />
    </ToastProvider>
  );
}

export default App;
